import React, { useState, useMemo } from 'react';
import {
  Users,
  Plus,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  UserPlus,
  X,
  Search,
} from 'lucide-react';
import { useTeams } from '../hooks/useTeams';
import { formatDate } from '../utils/formatters';
import { matchesEmployeeSearch } from '../utils/employeeDepartment';

function employeeLabel(emp) {
  if (!emp) return '—';
  const first = emp.first_name || emp.primer_nombre || '';
  const last = emp.last_name || emp.apellido_paterno || '';
  const full = `${first} ${last}`.trim();
  return full || emp.name || emp.email || `ID ${emp.id}`;
}

function departmentLabel(team) {
  return team?.department?.name || team?.Department?.name || '—';
}

function leaderLabel(team, factoryEmployees = []) {
  const leaderId = team?.leader_id || team?.leader?.id || team?.Leader?.id;
  const leader = team?.leader || team?.Leader;
  if (leader && leaderId && Number(leader.id) === Number(leaderId)) {
    return employeeLabel(leader);
  }
  if (leaderId && factoryEmployees.length) {
    const found = factoryEmployees.find((e) => Number(e.id) === Number(leaderId));
    if (found) return employeeLabel(found);
  }
  return employeeLabel(leader);
}

const emptyTeamForm = {
  name: '',
  description: '',
  departmentId: '',
  leaderId: '',
};

const emptyMemberForm = {
  employeeId: '',
  startDate: new Date().toISOString().slice(0, 10),
};

export function TeamsSection({ employees: employeesFromList = null }) {
  const {
    factoryId,
    selectedFactory,
    teams,
    teamsLoading,
    teamsError,
    factoryDepartments,
    factoryEmployees,
    eligibleMembers,
    selectedTeam,
    selectedTeamId,
    setSelectedTeamId,
    memberSearchQuery,
    setMemberSearchQuery,
    members,
    membersLoading,
    membersError,
    createTeam,
    updateTeam,
    deleteTeam,
    addMember,
    removeMember,
    actionError,
    actionMessage,
    runAction,
    canManageTeams,
    isSupervisorTeamScoped,
  } = useTeams(employeesFromList);

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamForm, setTeamForm] = useState(emptyTeamForm);
  const [memberForm, setMemberForm] = useState(emptyMemberForm);
  const [leaderSearchQuery, setLeaderSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const leaderCandidates = useMemo(() => {
    let list = factoryEmployees;
    if (leaderSearchQuery.trim()) {
      list = list.filter((emp) => matchesEmployeeSearch(emp, leaderSearchQuery));
    }
    return list;
  }, [factoryEmployees, leaderSearchQuery]);

  const openCreateModal = () => {
    setEditingTeam(null);
    setTeamForm(emptyTeamForm);
    setLeaderSearchQuery('');
    setShowTeamModal(true);
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setTeamForm({
      name: team.name || '',
      description: team.description || '',
      departmentId: String(team.department_id || team.department?.id || ''),
      leaderId: String(team.leader_id || team.leader?.id || ''),
    });
    setLeaderSearchQuery('');
    setShowTeamModal(true);
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTeam) {
        await runAction(() =>
          updateTeam(editingTeam.id, {
            name: teamForm.name,
            description: teamForm.description,
            leaderId: teamForm.leaderId,
          })
        );
      } else {
        if (!teamForm.departmentId || !teamForm.leaderId) {
          throw new Error('Departamento y líder son obligatorios.');
        }
        const created = await runAction(() => createTeam(teamForm));
        if (created?.id) setSelectedTeamId(String(created.id));
      }
      setShowTeamModal(false);
    } catch {
      // error shown via actionError
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`¿Eliminar el equipo «${team.name}»?`)) return;
    setSaving(true);
    try {
      await runAction(() => deleteTeam(team.id));
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberForm.employeeId) return;
    setSaving(true);
    try {
      await runAction(() => addMember(memberForm));
      setMemberForm({ ...emptyMemberForm, startDate: new Date().toISOString().slice(0, 10) });
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (employeeId) => {
    if (!window.confirm('¿Quitar a este miembro del equipo?')) return;
    setSaving(true);
    try {
      await runAction(() => removeMember(employeeId));
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 mb-6">
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-400" />
              Equipos de trabajo
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              {isSupervisorTeamScoped
                ? 'Tu equipo de trabajo · Vista de supervisor (solo lectura)'
                : 'Gestión de equipos y miembros'}
              {selectedFactory ? (
                <>
                  {' '}
                  · Fábrica: <span className="text-white">{selectedFactory}</span>
                </>
              ) : (
                ' · Selecciona una fábrica en el header'
              )}
            </p>
          </div>
          {canManageTeams && (
            <button
              type="button"
              onClick={openCreateModal}
              disabled={!factoryId || saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nuevo equipo
            </button>
          )}
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {!factoryId && (
            <div className="flex items-center gap-2 text-amber-300 text-sm rounded-lg border border-amber-800/50 bg-amber-900/20 p-3">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Selecciona una fábrica en el header para filtrar y crear equipos.
            </div>
          )}

          {(actionError || actionMessage) && (
            <div
              className={`text-sm rounded-lg p-3 border ${
                actionError
                  ? 'border-red-800 bg-red-900/20 text-red-300'
                  : 'border-emerald-800/50 bg-emerald-900/20 text-emerald-300'
              }`}
            >
              {actionError || actionMessage}
            </div>
          )}

          {teamsLoading && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando equipos…
            </div>
          )}

          {teamsError && !teamsLoading && (
            <div className="flex items-center gap-2 text-red-300 text-sm rounded-lg border border-red-800 bg-red-900/20 p-3">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {teamsError.message || 'Error al cargar equipos.'}
            </div>
          )}

          {!teamsLoading && !teamsError && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-lg border border-gray-700 overflow-hidden">
                <div className="px-3 py-2 bg-gray-800/80 border-b border-gray-700 text-xs text-gray-400 uppercase tracking-wide">
                  Equipos ({teams.length})
                </div>
                {teams.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 text-center">
                    {isSupervisorTeamScoped
                      ? 'No tienes ningún equipo asignado como líder.'
                      : `No hay equipos${factoryId ? ' en esta fábrica' : ''}.`}
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-700/60 max-h-80 overflow-y-auto">
                    {teams.map((team) => {
                      const isSelected = String(team.id) === String(selectedTeamId);
                      return (
                        <li key={team.id}>
                          <button
                            type="button"
                            onClick={() => setSelectedTeamId(String(team.id))}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-700/40 transition-colors ${
                              isSelected ? 'bg-teal-900/20 border-l-2 border-teal-500' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-medium text-white truncate">{team.name}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {departmentLabel(team)} · Líder: {leaderLabel(team, factoryEmployees)}
                                </p>
                              </div>
                              <div className="flex shrink-0 gap-1">
                                {canManageTeams && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditModal(team);
                                      }}
                                      className="p-1.5 rounded hover:bg-gray-600 text-gray-400 hover:text-white"
                                      title="Editar"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTeam(team);
                                      }}
                                      className="p-1.5 rounded hover:bg-red-900/40 text-gray-400 hover:text-red-300"
                                      title="Eliminar"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-gray-700 overflow-hidden flex flex-col min-h-[200px]">
                <div className="px-3 py-2 bg-gray-800/80 border-b border-gray-700 text-xs text-gray-400 uppercase tracking-wide">
                  {selectedTeam ? `Miembros — ${selectedTeam.name}` : 'Miembros del equipo'}
                </div>

                {!selectedTeamId ? (
                  <p className="p-4 text-sm text-gray-500 text-center flex-1 flex items-center justify-center">
                    {isSupervisorTeamScoped
                      ? 'Selecciona tu equipo para ver sus miembros.'
                      : 'Selecciona un equipo para ver y gestionar sus miembros.'}
                  </p>
                ) : (
                  <div className="p-4 space-y-4 flex-1">
                    {membersLoading && (
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Cargando miembros…
                      </div>
                    )}

                    {membersError && !membersLoading && (
                      <p className="text-sm text-red-300">
                        {membersError.message || 'Error al cargar miembros.'}
                      </p>
                    )}

                    {!membersLoading && !membersError && (
                      <>
                        {members.length === 0 ? (
                          <p className="text-sm text-gray-500">Este equipo no tiene miembros.</p>
                        ) : (
                          <ul className="space-y-2 max-h-40 overflow-y-auto">
                            {members.map((m) => (
                              <li
                                key={m.id}
                                className="flex items-center justify-between gap-2 rounded-lg bg-gray-700/40 px-3 py-2 text-sm"
                              >
                                <div className="min-w-0">
                                  <p className="text-white truncate flex items-center gap-2">
                                    <span className="truncate">
                                      {employeeLabel(m.employee || m.Employee)}
                                    </span>
                                    {selectedTeam &&
                                      Number(m.employee_id || m.employee?.id) ===
                                        Number(selectedTeam.leader_id || selectedTeam.leader?.id) && (
                                        <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-900/50 text-teal-300 border border-teal-700/60">
                                          Líder
                                        </span>
                                      )}
                                  </p>
                                  {m.start_date && (
                                    <p className="text-xs text-gray-500">
                                      Desde {formatDate(m.start_date)}
                                    </p>
                                  )}
                                </div>
                                {canManageTeams && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveMember(m.employee_id || m.employee?.id)
                                    }
                                    disabled={saving}
                                    className="p-1.5 rounded hover:bg-red-900/40 text-gray-400 hover:text-red-300 disabled:opacity-50"
                                    title="Quitar del equipo"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}

                        {canManageTeams && (
                          <form
                            onSubmit={handleAddMember}
                            className="border-t border-gray-700 pt-4 space-y-3"
                          >
                          <p className="text-xs font-medium text-gray-400 flex items-center gap-1">
                            <UserPlus className="w-3.5 h-3.5" />
                            Añadir miembro
                          </p>

                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <input
                              type="text"
                              value={memberSearchQuery}
                              onChange={(e) => setMemberSearchQuery(e.target.value)}
                              placeholder="Buscar empleado…"
                              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white text-sm"
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <select
                              value={memberForm.employeeId}
                              onChange={(e) =>
                                setMemberForm((f) => ({ ...f, employeeId: e.target.value }))
                              }
                              className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white text-sm sm:col-span-2"
                              required
                            >
                              <option value="">
                                {eligibleMembers.length
                                  ? 'Seleccionar empleado…'
                                  : 'No hay empleados disponibles'}
                              </option>
                              {eligibleMembers.map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                  {employeeLabel(emp)}
                                </option>
                              ))}
                            </select>
                            <input
                              type="date"
                              value={memberForm.startDate}
                              onChange={(e) =>
                                setMemberForm((f) => ({ ...f, startDate: e.target.value }))
                              }
                              className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white text-sm sm:col-span-2"
                              required
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={saving || !eligibleMembers.length}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm disabled:opacity-50"
                          >
                            {saving ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserPlus className="w-4 h-4" />
                            )}
                            Añadir
                          </button>
                        </form>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showTeamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">
                {editingTeam ? 'Editar equipo' : 'Nuevo equipo'}
              </h3>
              <button
                type="button"
                onClick={() => setShowTeamModal(false)}
                className="p-2 rounded-lg hover:bg-gray-700 text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveTeam} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Descripción</label>
                <textarea
                  value={teamForm.description}
                  onChange={(e) => setTeamForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white resize-none"
                />
              </div>
              {!editingTeam && (
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Departamento *</label>
                  <select
                    value={teamForm.departmentId}
                    onChange={(e) =>
                      setTeamForm((f) => ({
                        ...f,
                        departmentId: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white"
                    required
                  >
                    <option value="">Seleccionar…</option>
                    {factoryDepartments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Líder *</label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    value={leaderSearchQuery}
                    onChange={(e) => setLeaderSearchQuery(e.target.value)}
                    placeholder="Buscar empleado…"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white text-sm"
                  />
                </div>
                <select
                  value={teamForm.leaderId}
                  onChange={(e) => setTeamForm((f) => ({ ...f, leaderId: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white"
                  required
                >
                  <option value="">
                    {leaderCandidates.length
                      ? 'Seleccionar empleado…'
                      : 'No hay empleados en esta fábrica'}
                  </option>
                  {leaderCandidates.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {employeeLabel(emp)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowTeamModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving || (!editingTeam && !factoryId)}
                  className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
